import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  fetchFavorites, addFavorite, removeFavorite,
  searchMasterByName, clearSearch,
} from "../../../../redux/slices/favoritesSlice";
import { fetchPublicMasterLogo } from "../../../../redux/slices/masterLogoSlice";
import { fetchPublicBusinessLocation } from "../../../../redux/slices/businessLocationSlice";
import { fetchPublicSpecialities } from "../../../../redux/slices/specialitiesSlice";
import { getServerUrl } from "../../../../Utils/serverSelector";
import { fetchAppointmentsByPhone } from "../../../../redux/slices/appointmentsSlice";
import { fetchPublicMaster } from "../../../../redux/slices/masterSlice";
import { createBookingHandoff } from "../../../../redux/slices/userSessionSlice";

/* ══════════════════════════════════════════
   ПЕРЕВОДЫ — все три языка
══════════════════════════════════════════ */
const TR = {
  frequent_masters: { ru: "Часто посещаемые", he: "מאסטרים תכופים", en: "Frequent masters" },
  search_master: { ru: "Поиск мастера", he: "חיפוש מאסטר", en: "Search master" },
  search_ph: { ru: "Введите имя или фамилию мастера...", he: "הקלד שם של המאסטר...", en: "Enter master's name..." },
  search_results: { ru: "Результаты поиска", he: "תוצאות חיפוש", en: "Search results" },
  favorites: { ru: "Избранные", he: "מועדפים", en: "Favorites" },
  no_results: { ru: "Ничего не найдено", he: "לא נמצאו תוצאות", en: "No results found" },
  no_favorites: { ru: "Добавьте мастеров в избранное", he: "הוסף מאסטרים למועדפים", en: "Add masters to favorites" },
  address: { ru: "Адрес", he: "כתובת", en: "Address" },
  services: { ru: "Услуги", he: "שירותים", en: "Services" },
  last_visit: { ru: "Последний визит", he: "ביקור אחרון", en: "Last visit" },
  reviews: { ru: "отзывов", he: "ביקורות", en: "reviews" },
  book_now: { ru: "Записаться", he: "לקבוע תור", en: "Book now" },
  master_profile: { ru: "Профиль мастера", he: "פרופיל המאסטר", en: "Master profile" },
  add_fav: { ru: "В избранное", he: "למועדפים", en: "Add to favorites" },
  remove_fav: { ru: "Убрать из избранного", he: "הסר ממועדפים", en: "Remove from favorites" },
  contact: { ru: "Связаться", he: "צור קשר", en: "Contact" },
  nearest_slot: { ru: "Ближайшее время", he: "התור הקרוב", en: "Nearest slot" },

};

const toIntlIL = (raw) => {
  let p = String(raw || "").replace(/[^\d]/g, "");
  if (!p) return null;
  if (p.startsWith("972")) return p;
  if (p.startsWith("0")) return "972" + p.slice(1);
  return p;
};

const normPhone = (p) => {
  let s = String(p || "").replace(/[^\d]/g, "");
  if (!s) return "";
  if (s.startsWith("972")) return "+972" + s.slice(3);
  if (s.startsWith("0")) return "+972" + s.slice(1);
  return "+" + s;
};

const tr = (key, lang) => TR[key]?.[lang] || TR[key]?.ru || key;

const visitsLabel = (count, lang) => {
  const n = Number(count);
  if (lang === "he") return `${n} ביקורים`;
  if (lang === "en") return `${n} visit${n !== 1 ? "s" : ""}`;
  if (n === 1) return `${n} визит`;
  if (n >= 2 && n <= 4) return `${n} визита`;
  return `${n} визитов`;
};

/* ─── Вспомогательные ─────────────────────────────────── */
const getInitials = (firstName = "", lastName = "") =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";

const renderStars = (r) =>
  "★".repeat(Math.floor(Number(r))) + (Number(r) % 1 >= 0.5 ? "½" : "");

/* ─── Хук: загрузка фото мастера ──────────────────────── */
function useMasterImage(uid) {
  const dispatch = useDispatch();
  const [photoUrl, setPhotoUrl] = useState(null);
  const logoUrl = useSelector(s => s.masterLogo?.logoUrl);

  useEffect(() => {
    if (!uid) return;

    const loadPhoto = async () => {
      try {
        const res = await fetch(`${getServerUrl()}/public/${uid}/master-photo`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.filePath) {
            setPhotoUrl(`${getServerUrl()}${data.filePath}`);
            return;
          }
        }
      } catch { }

      dispatch(fetchPublicMasterLogo(uid));
    };

    loadPhoto();
  }, [uid, dispatch]);

  return photoUrl || logoUrl || null;
}

/* ─── Чип частого мастера ─────────────────────────────── */
function FreqChip({ master, visits, onClick, isFav, onToggleFav, lang }) {
  const imageUrl = useMasterImage(master?.uid);

  return (
<div className="fm-freq-chip" onClick={onClick} role="button" tabIndex={0} style={{ position: 'relative', cursor: 'pointer', paddingInlineEnd: 34 }}>      {imageUrl
        ? <img className="fm-freq-ava-img" src={imageUrl} alt="" />
        : <div className="fm-freq-ava">{getInitials(master.firstName, master.lastName)}</div>
      }
      <div>
        <div className="fm-freq-name">{master.firstName} {master.lastName}</div>
        <div className="fm-freq-cnt">{visits}</div>
      </div>
     <button
        onClick={(e) => { e.stopPropagation(); onToggleFav(master.uid); }}
        title={isFav ? tr("remove_fav", lang) : tr("add_fav", lang)}
        style={{
          position: 'absolute',
          top: 6,
          insetInlineEnd: 6, // в RTL сам уедет влево
          width: 22,
          height: 22,
          padding: 0,
          border: 'none',
          borderRadius: '50%',
          background: isFav ? '#ffe9e6' : '#f1f3f7',
          color: isFav ? '#e8836f' : '#a5b4c8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        {isFav ? "♥" : "♡"}
      </button>
    </div>
  );
}

/* ─── Карточка мастера ────────────────────────────────── */
function MasterCard({ item, isFav, onToggleFav, lang, onBookMaster }) {
  const dispatch = useDispatch();
  const master = item.master || item;
  const uid = master?.uid;

  const imageUrl = useMasterImage(uid);

  const specialities = useSelector(s => s.specialities?.byUid?.[uid] || []);

  const businessLocation = useSelector(
    s => s.businessLocation?.businessLocationsByUid?.[uid]
  );

  const allReviews = useSelector(s => s.reviews?.reviews || []);
  const masterReviews = useMemo(
    () => allReviews.filter(r => r.uid === uid || r.masterId === uid),
    [allReviews, uid]
  );

  const rating = useMemo(() => {
    if (!masterReviews.length) return null;
    const avg = masterReviews.reduce((acc, r) => acc + (Number(r.rating) || 0), 0) / masterReviews.length;
    return avg.toFixed(1);
  }, [masterReviews]);

  useEffect(() => {
    if (!uid) return;
    dispatch(fetchPublicSpecialities(uid));
    dispatch(fetchPublicBusinessLocation(uid));
  }, [uid, dispatch]);


  const specialty = specialities?.[0]?.profession || "";
  const address = businessLocation
    ? [businessLocation.city, businessLocation.address].filter(Boolean).join(", ")
    : "";

  return (
    <div className="master-card">
      <div className="master-card__bar" />

      {/* Шапка */}
      <div className="master-card__head">
        {imageUrl
          ? <img className="master-card__ava-img" src={imageUrl} alt="" />
          : <div className="master-card__ava">{getInitials(master.firstName, master.lastName)}</div>
        }

        <div className="master-card__info">
          <div className="master-card__name">{master.firstName} {master.lastName}</div>
          {specialty && <div className="master-card__spec">{specialty}</div>}
          {rating && (
            <div className="master-card__stars">
              <span className="master-card__stars-ico">{renderStars(rating)}</span>
              <span className="master-card__stars-val">{rating}</span>
              <span className="master-card__stars-cnt">· {masterReviews.length} {tr("reviews", lang)}</span>
            </div>
          )}
        </div>

        <button
          className={`master-card__fav ${isFav ? "master-card__fav--active" : ""}`}
          onClick={() => onToggleFav(uid)}
          title={isFav ? tr("remove_fav", lang) : tr("add_fav", lang)}
        >
          {isFav ? "♥" : "♡"}
        </button>
      </div>

      {/* Детали */}
      <div className="master-card__body">
        {address && (
          <div className="master-card__row">
            <div className="master-card__row-ico">
              <svg viewBox="0 0 24 24"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
            </div>
            <div className="master-card__row-text">
              <span className="master-card__row-lbl">{tr("address", lang)}</span>
              <span className="master-card__row-val">{address}</span>
            </div>
          </div>
        )}

        {master.services?.length > 0 && (
          <div className="master-card__row">
            <div className="master-card__row-ico">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            </div>
            <div className="master-card__row-text">
              <span className="master-card__row-lbl">{tr("services", lang)}</span>
              <div className="master-card__tags">
                {master.services.map(s => <span key={s} className="master-card__tag">{s}</span>)}
              </div>
            </div>
          </div>
        )}

        {item.nextSlot && (
          <div className="master-card__row">
            <div className="master-card__row-ico">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            </div>
            <div className="master-card__row-text">
              <span className="master-card__row-lbl">{tr("nearest_slot", lang)}</span>
              <span className="master-card__row-val">
                {new Date(item.nextSlot.date).toLocaleDateString()} · {item.nextSlot.time}
              </span>
            </div>
          </div>
        )}

        {item.lastVisit && (
          <div className="master-card__row">
            <div className="master-card__row-ico">
              <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            </div>
            <div className="master-card__row-text">
              <span className="master-card__row-lbl">{tr("last_visit", lang)}</span>
              <span className="master-card__row-val">{new Date(item.lastVisit).toLocaleDateString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Кнопки */}
      <div className="master-card__actions">
        {/* ─── Записаться — navigate внутри приложения ─── */}
        <button
          className="master-card__btn master-card__btn--book"
          onClick={() => onBookMaster(master.masterLink)}
          disabled={!master.masterLink}
          style={{ opacity: master.masterLink ? 1 : 0.45 }}
        >
          ✓ {tr("book_now", lang)}
        </button>

        <button
          className="master-card__btn master-card__btn--profile"
          onClick={() => {
            const num = toIntlIL(master.phone);
            if (num) window.open(`https://wa.me/${num}`, "_blank", "noopener,noreferrer");
          }}
          disabled={!master.phone}
          style={{ opacity: master.phone ? 1 : 0.45 }}
        >
          💬 {tr("contact", lang)}
        </button>
      </div>
    </div>
  );
}

/* ─── Главный компонент ───────────────────────────────── */
export default function FavoriteMasters() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "ru";
  const dispatch = useDispatch();

  const client = useSelector(s => s.userSession?.currentAccount);
  const favorites = useSelector(s => s.favorites?.list || []);
  const loading = useSelector(s => s.favorites?.loading);
  const searchResults = useSelector(s => s.favorites?.searchResults || []);
  const searchLoading = useSelector(s => s.favorites?.searchLoading);
  const allAppointments = useSelector(s => s.appointments?.allAppointments || []);

  const [query, setQuery] = useState("");
  const [freqMasters, setFreqMasters] = useState({});

  useEffect(() => {
    if (client?.uid) dispatch(fetchFavorites(client.uid));
  }, [client?.uid, dispatch]);

  // ✅ грузим записи (для «часто посещаемых») и список мастеров (для чипов)
  useEffect(() => {
    if (client?.phone) dispatch(fetchAppointmentsByPhone(client.phone));
  }, [client?.phone, dispatch]);

  useEffect(() => () => dispatch(clearSearch()), [dispatch]);

  useEffect(() => {
    if (query.trim().length < 2) { dispatch(clearSearch()); return; }
    const timer = setTimeout(() => dispatch(searchMasterByName(query.trim())), 400);
    return () => clearTimeout(timer);
  }, [query, dispatch]);


  const frequentMasters = useMemo(() => {
    if (!client?.phone) return [];
    const myPhone = normPhone(client.phone);
    const countMap = {};
    allAppointments
      .filter(a => normPhone(a?.phoneContact) === myPhone)
      .forEach(a => { if (a.uid) countMap[a.uid] = (countMap[a.uid] || 0) + 1; });
    return Object.entries(countMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([uid, count]) => ({ uid, count }));
  }, [allAppointments, client?.phone]);

  useEffect(() => {
    frequentMasters.forEach(({ uid }) => {
      if (freqMasters[uid]) return;
      dispatch(fetchPublicMaster(uid))
        .unwrap()
        .then(m => setFreqMasters(prev => ({ ...prev, [uid]: m })))
        .catch(() => { });
    });
  }, [frequentMasters, freqMasters, dispatch]);


  const favMasterIds = useMemo(
    () => new Set(favorites.map(f => f.masterId)),
    [favorites]
  );

  const handleToggleFav = useCallback(async (masterId) => {
    if (!client?.uid) return;
    if (favMasterIds.has(masterId)) {
      await dispatch(removeFavorite({ userId: client.uid, masterId }));
    } else {
      await dispatch(addFavorite({ userId: client.uid, masterId }));
    }
    // перезагружаем избранное, чтобы карточка пришла с полными данными мастера
    dispatch(fetchFavorites(client.uid));
  }, [client?.uid, favMasterIds, dispatch]);


  // ─── записаться — переход на co.il с одноразовым токеном авторизации ───
  // ─── записаться — переход на co.il с одноразовым токеном авторизации ───
  const handleBookMaster = useCallback(async (masterLink) => {
    if (!masterLink) return;
    const path = String(masterLink).replace(/^\/+/, "");
    let url = `https://zmanup.co.il/${lang}/${path}?from=cabinet`;
    try {
      const handoffToken = await dispatch(createBookingHandoff()).unwrap();
      if (handoffToken) url += `&ct=${handoffToken}`;
    } catch { /* не залогинен / ошибка — уходим гостем */ }
    window.location.assign(url);
  }, [lang, dispatch]);

  const isSearching = query.trim().length >= 2;
  const showList = isSearching ? searchResults : favorites;

  return (
    <div className="favorites-container">

      {/* Часто посещаемые */}
      {frequentMasters.length > 0 && (
        <>
          <p className="cd-sec-title">{tr("frequent_masters", lang)}</p>
          <div className="fm-freq-row">
            {frequentMasters.map(({ uid, count }) => {
              const master = freqMasters[uid];
              if (!master) return null;
              return (
                <FreqChip
                  key={uid}
                  master={master}
                  visits={visitsLabel(count, lang)}
                  onClick={() => handleBookMaster(master.masterLink)}
                  isFav={favMasterIds.has(uid)}
                  onToggleFav={handleToggleFav}
                  lang={lang}
                />
              );
            })}
          </div>
        </>
      )}

      {/* Поиск */}
      <p className="cd-sec-title" style={{ marginTop: frequentMasters.length ? 16 : 0 }}>
        {tr("search_master", lang)}
      </p>
      <div className="fm-search-box">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a5b4c8" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={tr("search_ph", lang)}
          className="fm-search-input"
        />
        {query && (
          <button className="fm-search-clear" onClick={() => setQuery("")}>✕</button>
        )}
        {searchLoading && <div className="fm-search-spinner" />}
      </div>

      {/* Список */}
      <p className="cd-sec-title" style={{ marginTop: 16 }}>
        {isSearching ? tr("search_results", lang) : tr("favorites", lang)}
      </p>

      {loading ? (
        <div className="fm-loading"><div className="cp-avatar-spinner" /></div>
      ) : showList.length === 0 ? (
        <div className="fm-empty">
          {isSearching ? tr("no_results", lang) : tr("no_favorites", lang)}
        </div>
      ) : (
        <div className="favorites-grid">
          {showList.map(item => (
            <MasterCard
              key={item.id || item.masterId || item.uid}
              item={item}
              isFav={favMasterIds.has(item.masterId || item.uid)}
              onToggleFav={handleToggleFav}
              lang={lang}
              onBookMaster={handleBookMaster}
            />
          ))}
        </div>
      )}
    </div>
  );
}